import React, { useEffect, useState } from "react";

import { Divider, Typography } from "@mui/material";
import {
  GetSubmissionResponsePortal,
  SubmissionAttachment,
  SubmissionSummary,
} from "@rubi/api-generated";
import { colors } from "@rubi/ui";

import AlertCardMedia from "@/components/alerts/BulletinCard/ImageGallery";

import styles from "./ReportCard.module.scss";

const drugColorMapping = {
  "Bath Salts": colors.divider, // Light pink
  Cocaine: colors.primary.contrastText, // White
  "Crack Cocaine": colors.divider, // Gold
  "Drug Press and Press Platers": colors.divider, // Light gray
  Fentanyl: colors.secondary.dark, // Blue
  GHB: colors.secondary.main, // Blue violet
  Hashish: colors.action.disabled, // Burlywood
  Heroin: colors.legacy.coral, // Red
  Ketamine: colors.secondary.light, // Medium slate blue
  Khat: colors.success.light, // Lime green
  "KroKodil (Desomophine)": colors.warning.light, // Goldenrod
  LSD: colors.warning.dark, // Orange red
  Marijuana: colors.success.main, // Forest green
  MDA: colors.text.disabled, // Hot pink
  "MDMA / Molly / Ecstasy": colors.secondary.light, // Medium orchid
  "Mescaline / Peyote": colors.divider, // Peach puff
  Methamphetamine: colors.divider, // Chartreuse
  Other: colors.action.disabled, // Light steel blue
  PCP: colors.secondary.light, // Magenta
  "Pills and Tablets": colors.warning.light, // Orange
  "Prescription Drugs": colors.primary.light, // Dodger blue
  Psilocybin: colors.secondary.light, // Medium purple
  Steroids: colors.error.light, // Tomato
  "THC Eatables": colors.action.active, // Deep pink
  Unknown: colors.divider, // Light gray
};

interface GoogleMapsPlace {
  name?: string;
  formatted_address?: string;
}

interface LocationAddress {
  googleMapsPlace?: GoogleMapsPlace;
}

interface ReportAnswerData {
  incident_date?: string;
  incident_drugType?: keyof typeof drugColorMapping;
  incident_drugImpressions?: string;
  incident_evidences?: unknown;
  incident_location_address?: LocationAddress;
  incident_seizureAmount?: string;
  incident_seizureType?: string;
  incident_measurementType?: string;
  incident_occurrenceNumber?: string;
  incident_packagingMarkings?: string;
  incident_packagingMaterial?: string;
  incident_quantitativeAnalysis?: string;
  incident_remarks?: string;
  person_reportingParty_agency?: string;
  person_reportingParty_email?: string;
  person_reportingParty_firstName?: string;
  person_reportingParty_lastName?: string;
  person_reportingParty_phoneNumber?: string;
  person_reportingParty_unit?: string;
  "person-reportingParty_agency"?: string;
  "person-reportingParty_email"?: string;
  "person-reportingParty_firstName"?: string;
  "person-reportingParty_lastName"?: string;
  "person-reportingParty_phoneNumber"?: string;
  "person-reportingParty_unit"?: string;
  report_shareDEA?: boolean;
  report_shareOtherPolice?: boolean;
}

interface ReportCardProps {
  report: SubmissionSummary;
  onClick: () => void;
  index: number;
  reportType: string;
  className?: string;
}

const generateSummary = (report: SubmissionSummary | null | undefined) => {
  if (!report || !report.latest_data) {
    return null;
  }

  const {
    incident_date,
    incident_drugType,
    incident_location_address,
    incident_seizureAmount,
    incident_measurementType,
  } = report.latest_data as ReportAnswerData;

  const location = incident_location_address?.googleMapsPlace;
  const locationDescription = `${location?.name}, ${location?.formatted_address}`;

  return (
    <>
      <Typography variant="body2">
        <strong>Date:</strong> {incident_date}
      </Typography>
      <Typography variant="body2">
        <strong>Drug Type:</strong> {incident_drugType}
      </Typography>
      <Typography variant="body2">
        <strong>Seizure Amount:</strong> {incident_seizureAmount}{" "}
        {incident_measurementType}
      </Typography>
      <Typography variant="body2">
        <strong>Location:</strong> {locationDescription}
      </Typography>
    </>
  );
};

const generateDetails = (report: GetSubmissionResponsePortal) => {
  if (!report.submission) {
    return null;
  }

  const {
    incident_drugImpressions,
    incident_quantitativeAnalysis,
    incident_occurrenceNumber,
    incident_packagingMaterial,
    incident_packagingMarkings,
    incident_remarks,
    person_reportingParty_agency,
    person_reportingParty_email,
    person_reportingParty_firstName,
    person_reportingParty_lastName,
    person_reportingParty_phoneNumber,
    person_reportingParty_unit,
    report_shareDEA,
    report_shareOtherPolice,
  } = report.submission as ReportAnswerData;

  return (
    <>
      <Typography variant="body2">
        <strong>Drug Impressions:</strong> {incident_drugImpressions}
      </Typography>
      <Typography variant="body2">
        <strong>Quantitative Analysis:</strong> {incident_quantitativeAnalysis}
      </Typography>
      <Typography variant="body2">
        <strong>Occurrence Number:</strong> {incident_occurrenceNumber}
      </Typography>
      <Typography variant="body2">
        <strong>Packaging Material:</strong> {incident_packagingMaterial}
      </Typography>
      <Typography variant="body2">
        <strong>Packaging Markings:</strong> {incident_packagingMarkings}
      </Typography>
      <Typography variant="body2">
        <strong>Remarks:</strong> {incident_remarks}
      </Typography>
      <Typography variant="body2">
        <strong>Reporting Party:</strong>
      </Typography>
      <Typography variant="body2">
        - <strong>Agency:</strong> {person_reportingParty_agency}
      </Typography>
      <Typography variant="body2">
        - <strong>Unit:</strong> {person_reportingParty_unit}
      </Typography>
      <Typography variant="body2">
        - <strong>Name:</strong> {person_reportingParty_firstName}{" "}
        {person_reportingParty_lastName}
      </Typography>
      <Typography variant="body2">
        - <strong>Email:</strong> {person_reportingParty_email}
      </Typography>
      <Typography variant="body2">
        - <strong>Phone:</strong> {person_reportingParty_phoneNumber}
      </Typography>
      <Typography variant="body2">
        <strong>Shared with:</strong>
      </Typography>
      <Typography variant="body2">
        - <strong>DEA:</strong> {report_shareDEA ? "Yes" : "No"}
      </Typography>
      <Typography variant="body2">
        - <strong>Other Police:</strong>{" "}
        {report_shareOtherPolice ? "Yes" : "No"}
      </Typography>
    </>
  );
};

const ReportCard: React.FC<ReportCardProps> = ({
  report,
  onClick,
  index,
  reportType,
  className,
}) => {
  // const [expanded, setExpanded] = useState(false);
  const [reportImages, setReportImages] = useState<any>([]);

  const {
    incident_date,
    incident_drugImpressions,
    incident_drugType,
    incident_evidences,
    incident_location_address,
    incident_measurementType,
    incident_occurrenceNumber,
    incident_packagingMarkings,
    incident_packagingMaterial,
    incident_quantitativeAnalysis,
    incident_remarks,
    incident_seizureAmount,
    incident_seizureType,
    "person-reportingParty_agency": person_reportingParty_agency,
    "person-reportingParty_email": person_reportingParty_email,
    "person-reportingParty_firstName": person_reportingParty_firstName,
    "person-reportingParty_lastName": person_reportingParty_lastName,
    "person-reportingParty_phoneNumber": person_reportingParty_phoneNumber,
    "person-reportingParty_unit": person_reportingParty_unit,
    report_shareDEA,
    report_shareOtherPolice,
  } = (report.latest_data || {}) as ReportAnswerData;

  const getReportMedia = () => {
    if (reportImages.length > 0) {
      return;
    }
    const tempData: { download_url: string }[] = [];
    for (const attachment of report.attachments.sort(
      (a: SubmissionAttachment, b: SubmissionAttachment) => b.size - a.size,
    )) {
      if (!attachment.content_type?.includes("image")) {
        continue;
      }
      const downloadUrl =
        attachment.thumbnail_presigned_get?.s3_presigned.uri ??
        attachment.presigned_get?.s3_presigned.uri;

      if (!downloadUrl) {
        continue;
      }

      tempData.push({
        download_url: downloadUrl,
      });

      if (tempData.length >= 4) {
        break;
      }
    }
    setReportImages(tempData);
  };

  useEffect(() => {
    getReportMedia();
  }, [report.attachments]);

  // const handleExpandClick = () => {
  //   setExpanded(!expanded);
  // };

  return (
    <div className={`${styles.alert_item} ${className}`}>
      <div
        className={styles.content}
        onClick={() => {
          if (window.getSelection()?.toString().length === 0) {
            onClick();
          }
        }}
      >
        {incident_drugType && (
          <div
            className={styles.alert_title}
            style={{ background: drugColorMapping[incident_drugType] }}
          >
            {incident_drugType}
          </div>
        )}
        <div className={styles.alert_image_parent}>
          {reportImages.length > 0 ? (
            <AlertCardMedia
              mediaDetails={reportImages.map((image) => ({
                media_type: "image" as const,
                download_url: image.download_url,
              }))}
              resolved={false}
            />
          ) : (
            <div className={styles.alert_description}>
              <Typography variant="body2">{generateSummary(report)}</Typography>
            </div>
          )}
        </div>

        <div className={styles.alert_action}>
          <Divider />
          <div className={styles.alert_info}>
            {person_reportingParty_agency && (
              <div className={styles.truncate}>
                <span className={styles.alert_title}>
                  {person_reportingParty_agency}
                </span>{" "}
              </div>
            )}

            {incident_location_address && (
              <div className={styles.truncate}>
                <span className={styles.alert_title}>
                  {incident_location_address.googleMapsPlace?.name}
                </span>{" "}
              </div>
            )}
            {/* add date*/}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-end",
              }}
            >
              <div className={styles.alert_date_parent}>
                <Typography variant="caption">{incident_date}</Typography>
              </div>
              {(report.similarity || incident_occurrenceNumber) && (
                <div className={styles.truncate}>
                  <span
                    className={styles.alert_title}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-end",
                    }}
                  >
                    {report.similarity
                      ? `Similarity: ${Math.round(report.similarity * 100)}%`
                      : incident_occurrenceNumber
                        ? `${incident_occurrenceNumber}`
                        : ""}
                  </span>
                  <span
                    className={styles.alert_title}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-end",
                    }}
                  >
                    {incident_occurrenceNumber}
                  </span>{" "}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportCard;
